import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { AuthForm, AuthMode } from '@/components/auth/AuthForm';

const REDIRECT_WHITELIST = [
  /^https?:\/\/([a-z0-9-]+\.)*heyjoe\.io(\/|$)/i,
  /^https?:\/\/([a-z0-9-]+\.)*syngency\.com(\/|$)/i,
  /^https?:\/\/([a-z0-9-]+\.)*limelite\.com(\/|$)/i,
];

function isAllowedRedirect(uri: string): boolean {
  try {
    const url = new URL(uri);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return false;
    return REDIRECT_WHITELIST.some((re) => re.test(uri));
  } catch {
    return false;
  }
}

const AuthPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isLoading } = useAuthContext();
  // Router state is set by RequireAuth when it bounces someone off a guarded route.
  // A ?returnTo= is what a plain <a> or window.location redirect can carry, and
  // without it a deadline offer sends people to the dashboard after they sign in
  // instead of back to the thing they were trying to buy. Same-origin paths only,
  // so this can never be turned into an open redirect.
  const rawReturn = searchParams.get('returnTo') || '';
  const safeReturn = rawReturn.startsWith('/') && !rawReturn.startsWith('//') ? rawReturn : '';
  const returnTo = (location.state as any)?.from || safeReturn || '/';

  const initialMode = (searchParams.get('mode') === 'register' ? 'register' : 'login') as AuthMode;
  const initialEmail = searchParams.get('email') || '';
  const redirectUri = searchParams.get('redirect_uri') || '';

  const [mode, setMode] = useState<AuthMode>(initialMode);

  // Already signed in? Don't show the login form — bounce home (or complete an
  // SSO hand-off if we arrived with a redirect_uri). Wait for the auth check to
  // finish so we don't redirect before a stored token has been validated.
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    if (redirectUri && isAllowedRedirect(redirectUri)) {
      const token = localStorage.getItem('authToken');
      if (token) {
        const separator = redirectUri.includes('?') ? '&' : '?';
        window.location.href = `${redirectUri}${separator}token=${encodeURIComponent(token)}`;
        return;
      }
    }
    navigate(returnTo, { replace: true });
  }, [isAuthenticated, isLoading]);

  const handleSuccess = (token: string, user: any) => {
    login(token, user);

    if (redirectUri && isAllowedRedirect(redirectUri)) {
      const separator = redirectUri.includes('?') ? '&' : '?';
      window.location.href = `${redirectUri}${separator}token=${encodeURIComponent(token)}`;
    } else {
      navigate(returnTo);
    }
  };

  const title =
    mode === 'login' ? 'Sign in.' :
    mode === 'register' ? 'Make an account.' :
    'Reset password.';
  const subtitle =
    mode === 'login' ? '' :
    mode === 'register' ? 'Free account. Start using the tools in under a minute.' :
    "We'll email you a link to set a new password.";

  return (
    <main
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)' }}
    >
      <div className="w-full" style={{ maxWidth: 440 }}>
        <div className="text-center" style={{ marginBottom: 24 }}>
          <img
            src="/brand/wordmark-heyjoe.png"
            alt="Hey Joe"
            className="mx-auto dark:[filter:invert(96%)_sepia(8%)_saturate(280%)_hue-rotate(345deg)_brightness(102%)]"
            style={{
              height: 40,
              width: 'auto',
              marginBottom: 22,
            }}
          />
          <h1
            className="font-hj-display m-0"
            style={{
              fontSize: 'clamp(36px, 5.4vw, 48px)',
              lineHeight: 1,
              color: 'var(--hj-ink)',
              letterSpacing: '-0.03em',
              fontWeight: 800,
            }}
          >
            {title}
          </h1>
          {subtitle && (
            <p
              className="m-0"
              style={{
                marginTop: 12,
                color: 'var(--hj-ink-2)',
                fontSize: 14,
                lineHeight: 1.5,
              }}
            >
              {subtitle}
            </p>
          )}
        </div>

        <div
          style={{
            padding: 24,
            borderRadius: 'var(--hj-r-md)',
            background: 'var(--hj-surface-1)',
            border: '1px solid var(--hj-hairline)',
          }}
        >
          <AuthForm
            mode={mode}
            onModeChange={setMode}
            onSuccess={handleSuccess}
            initialEmail={initialEmail}
          />
        </div>
      </div>
    </main>
  );
};

export default AuthPage;
